<script setup lang="ts">
/**
 * "บล็อกผู้ส่งนี้" in the web chat card (docs/webchat.md "บล็อกผู้ส่ง"). Used twice by WebVisitorCard:
 *   mode "banner"   top of the card, only while the visitor is blocked: until when, who, when, why, the IP part,
 *                   what the visitor sees · ยกเลิกการบล็อก (head/owner) · บล็อกนานขึ้น
 *   mode "control"  bottom of the card, while not blocked: spam-limit hits (to help decide) · the last block · the
 *                   quiet "บล็อกผู้ส่งนี้" button → dialog: 24 ชั่วโมง / 7 วัน / ถาวร + reason (+ IP, head/owner, with
 *                   the shared-mobile-IP warning)
 *   mode "menu"     the dialogs only, opened from the chat header's "…" menu (owner 17 ต.ค. 69: block control leaves
 *                   the panel) — `showBlock()`; the dialog says the spam-limit hits and the last block
 * The server decides who may do what (`can`); the options it would refuse are shown disabled with the reason.
 */
import { computed, ref, watch } from 'vue'
import type { BlockDuration, IpDuration } from '../../api/wcblock/types'
import { relTime } from '../../lib/webchat'
import {
  IP_DURATIONS,
  IP_WARNING,
  MANAGER_ONLY,
  REASON_MAX,
  bkkDateTime,
  cleanReason,
  defaultDuration,
  durationOptions,
  historyLine,
  hitsText,
  untilText,
} from '../../lib/wcblock'
import { useClock } from '../../stores/clock'
import { useWcBlock } from '../../stores/wcblock'
import { useWebchat } from '../../stores/webchat'
import LibDialog from '../library/LibDialog.vue'
import Icon from '../ui/Icon.vue'

const props = defineProps<{ convId: string; mode: 'banner' | 'control' | 'menu'; name?: string | null }>()
const store = useWcBlock()
const web = useWebchat()
const clock = useClock()

// the card reloads on its timer and when any screen changes this chat (socket): follow it
watch(
  [() => props.convId, () => web.visitors[props.convId]],
  ([id]) => {
    if (id) void store.load(id)
  },
  { immediate: true },
)

const st = computed(() => store.states[props.convId] ?? null)
const b = computed(() => st.value?.block ?? null)
const last = computed(() => st.value?.history[0] ?? null)
const hits = computed(() => st.value?.limit_hits ?? null)
const who = computed(() => props.name || 'ผู้ส่งนี้')

// ---- block dialog
const open = ref(false)
const duration = ref<BlockDuration | null>(null)
const reason = ref('')
const withIp = ref(false)
const ipFor = ref<IpDuration>('1h')
const error = ref<string | null>(null)
const options = computed(() => (st.value ? durationOptions(st.value, clock.now) : []))

function showBlock(): void {
  if (!st.value) return
  duration.value = b.value ? (options.value.find((o) => !o.disabled && o.key !== '24h')?.key ?? defaultDuration(st.value, clock.now))
    : defaultDuration(st.value, clock.now)
  reason.value = ''
  withIp.value = false
  ipFor.value = '1h'
  error.value = null
  open.value = true
}
async function doBlock(): Promise<void> {
  if (!duration.value) return
  error.value = await store.block(props.convId, {
    duration: duration.value,
    reason: cleanReason(reason.value),
    ip: withIp.value ? ipFor.value : null,
  })
  if (!error.value) open.value = false
}

// ---- unblock dialog
const unOpen = ref(false)
const note = ref('')
const unError = ref<string | null>(null)
function showUnblock(): void {
  note.value = ''
  unError.value = null
  unOpen.value = true
}
async function doUnblock(): Promise<void> {
  unError.value = await store.unblock(props.convId, cleanReason(note.value))
  if (!unError.value) unOpen.value = false
}

const rel = (iso: string) => relTime(iso, clock.now)

/** For the "…" menu: may this visitor be blocked now, and what helps decide (spam-limit hits). */
const canBlock = computed(() => !!st.value && !b.value && st.value.can.block)
const hitsLine = computed(() => (hits.value ? hitsText(hits.value, rel) : null))
defineExpose({ showBlock, canBlock, hitsLine })
</script>

<template>
  <!-- blocked: the banner at the top of the card -->
  <div v-if="mode === 'banner' && b" class="wb-banner" role="status" aria-label="ผู้ส่งนี้ถูกบล็อกอยู่">
    <div class="wb-h">
      <Icon name="minusCircle" />
      <span class="wb-t">บล็อกอยู่</span>
      <span class="wb-until">{{ untilText(b.until, clock.now) }}</span>
    </div>
    <dl class="wb-dl">
      <div>
        <dt>โดย</dt>
        <dd>{{ b.by.name }} · {{ bkkDateTime(b.at, clock.now) }}</dd>
      </div>
      <div>
        <dt>เหตุผล</dt>
        <dd :class="{ 'wb-none': !b.reason }">{{ b.reason || 'ไม่ได้ระบุ' }}</dd>
      </div>
      <div v-if="b.ip && b.ip.active && b.ip.until">
        <dt>รวม IP</dt>
        <dd>{{ b.ip.masked }} {{ untilText(b.ip.until, clock.now) }}</dd>
      </div>
    </dl>
    <p class="wb-note">ลูกค้าเห็นแค่ "ขออภัยค่ะ ขณะนี้ไม่สามารถส่งข้อความได้" และจะไม่เห็นข้อความตอบจากทีมระหว่างที่บล็อก</p>
    <div class="wb-actions">
      <button v-if="st?.can.unblock" type="button" class="btn wb-un" :disabled="store.busy" @click="showUnblock">
        <Icon name="undo" />ยกเลิกการบล็อก
      </button>
      <button
        v-if="!b.permanent && options.some((o) => !o.disabled)"
        type="button"
        class="btn"
        :disabled="store.busy"
        @click="showBlock"
      >
        <Icon name="clock" />{{ st?.can.unblock ? 'เปลี่ยนระยะเวลา' : 'บล็อกนานขึ้น' }}
      </button>
      <span v-if="!st?.can.unblock" class="wb-who">ยกเลิกได้เฉพาะผู้จัดการหรือเจ้าของ</span>
    </div>
  </div>

  <!-- not blocked: the quiet control at the bottom of the card -->
  <div v-else-if="mode === 'control' && st && !b" class="wb-ctl" role="group" aria-label="บล็อกผู้ส่ง">
    <div class="wb-sh">
      <Icon name="shield" />
      <span class="wb-sht">กันสแปม</span>
    </div>
    <p v-if="hits" class="wb-hits" :class="{ warn: hits.count > 0 }">
      <Icon v-if="hits.count > 0" name="alert" /><span>{{ hitsText(hits, rel) }}</span>
    </p>
    <p v-if="last" class="wb-last">เคยถูกบล็อก: {{ historyLine(last, clock.now) }}</p>
    <button type="button" class="wb-go" @click="showBlock"><Icon name="shield" /><span>บล็อกผู้ส่งนี้</span></button>
  </div>

  <LibDialog
    v-if="open && st"
    :title="b ? 'เปลี่ยนการบล็อก' : 'บล็อกผู้ส่งนี้'"
    :sub="`${who} · แชทจากหน้าเว็บ`"
    :width="480"
    :close-on-backdrop="!store.busy"
    @close="open = false"
  >
    <fieldset class="wb-f">
      <legend>ระยะเวลา</legend>
      <div class="wb-seg" role="radiogroup" aria-label="ระยะเวลาบล็อก">
        <label v-for="o in options" :key="o.key" class="wb-opt" :class="{ on: duration === o.key, off: o.disabled }" :title="o.why ?? undefined">
          <input v-model="duration" type="radio" name="wb-dur" :value="o.key" :disabled="o.disabled" />
          <span>{{ o.label }}</span>
        </label>
      </div>
      <p v-if="!st.can.forever" class="wb-hint">ถาวร: {{ MANAGER_ONLY }}</p>
    </fieldset>
    <div v-if="mode === 'menu' && !b && (hits?.count || last)" class="wb-f wb-ctx">
      <p v-if="hits?.count" class="wb-hits warn"><Icon name="alert" /><span>{{ hitsText(hits, rel) }}</span></p>
      <p v-if="last" class="wb-last">เคยถูกบล็อก: {{ historyLine(last, clock.now) }}</p>
    </div>
    <label class="wb-f wb-reason">
      <span class="wb-l">เหตุผล <span class="wb-opt-t">(ไม่บังคับ)</span></span>
      <textarea v-model="reason" rows="2" :maxlength="REASON_MAX" placeholder="เช่น ส่งลิงก์โฆษณาซ้ำ ๆ"></textarea>
    </label>
    <div v-if="st.can.ip" class="wb-f wb-ip">
      <label class="wb-check" :class="{ off: !st.ip_known }">
        <input v-model="withIp" type="checkbox" :disabled="!st.ip_known" />
        <span>
          บล็อก IP ล่าสุดของผู้ส่งนี้ด้วย<template v-if="st.ip_masked"> ({{ st.ip_masked }})</template>
          <span v-if="!st.ip_known" class="wb-hint">ยังไม่รู้ IP ของผู้ส่งนี้ บล็อกได้เฉพาะตัวผู้ส่ง</span>
        </span>
      </label>
      <template v-if="withIp">
        <div class="wb-seg sm" role="radiogroup" aria-label="ระยะเวลาบล็อก IP">
          <label v-for="o in IP_DURATIONS" :key="o.key" class="wb-opt" :class="{ on: ipFor === o.key }">
            <input v-model="ipFor" type="radio" name="wb-ip" :value="o.key" />
            <span>{{ o.label }}</span>
          </label>
        </div>
        <p class="wb-warn" role="note"><Icon name="alert" /><span>{{ IP_WARNING }}</span></p>
      </template>
    </div>
    <ul class="wb-about">
      <li><Icon name="eye" /><span>ผู้ส่งจะเห็นแค่ "ขออภัยค่ะ ขณะนี้ไม่สามารถส่งข้อความได้" ไม่มีคำว่าบล็อก</span></li>
      <li><Icon name="chat" /><span>ข้อความเดิมในแชทนี้ยังอยู่ครบ</span></li>
      <li><Icon name="check" /><span>ปิดแชทนี้ให้อัตโนมัติ ไม่นับเป็นแชทที่พลาดในแดชบอร์ด · ยกเลิกบล็อกแล้วแชทยังปิดอยู่</span></li>
      <li><Icon name="undo" /><span>ผู้จัดการหรือเจ้าของยกเลิกได้ทุกเมื่อ ที่การ์ดนี้หรือ ตั้งค่า › แชทบนเว็บ</span></li>
    </ul>
    <p v-if="error" class="wb-err" role="alert"><Icon name="alertCircle" /><span>{{ error }}</span></p>
    <template #footer>
      <span class="grow"></span>
      <button type="button" class="btn" :disabled="store.busy" @click="open = false">ยกเลิก</button>
      <button type="button" class="btn wb-do" :disabled="store.busy || !duration" @click="doBlock">
        <Icon name="shield" />{{ store.busy ? 'กำลังบล็อก…' : 'บล็อก' }}
      </button>
    </template>
  </LibDialog>

  <LibDialog
    v-if="unOpen"
    title="ยกเลิกการบล็อก"
    :sub="`${who} · แชทจากหน้าเว็บ`"
    :width="440"
    :close-on-backdrop="!store.busy"
    @close="unOpen = false"
  >
    <p class="wb-p">ผู้ส่งนี้จะกลับมาส่งข้อความได้ทันที</p>
    <label class="wb-f wb-reason">
      <span class="wb-l">หมายเหตุ <span class="wb-opt-t">(ไม่บังคับ)</span></span>
      <textarea v-model="note" rows="2" :maxlength="REASON_MAX" placeholder="เช่น ลูกค้าโทรมาแจ้งว่าส่งผิด"></textarea>
    </label>
    <p v-if="unError" class="wb-err" role="alert"><Icon name="alertCircle" /><span>{{ unError }}</span></p>
    <template #footer>
      <span class="grow"></span>
      <button type="button" class="btn" :disabled="store.busy" @click="unOpen = false">ปิด</button>
      <button type="button" class="btn wb-pri" :disabled="store.busy" @click="doUnblock">
        <Icon name="undo" />{{ store.busy ? 'กำลังยกเลิก…' : 'ยกเลิกการบล็อก' }}
      </button>
    </template>
  </LibDialog>
</template>

<style scoped>
/* ---- banner (blocked) */
.wb-banner {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px 12px 12px;
  border: 1px solid #f3c9c4;
  border-radius: 10px;
  background: var(--crits);
}
.wb-h {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px 8px;
}
.wb-h > :deep(.ic) {
  width: 16px;
  height: 16px;
  flex: none;
  color: var(--crit);
}
.wb-t {
  color: var(--crit);
  font-size: 14px;
  font-weight: 700;
  line-height: 21px;
}
.wb-until {
  color: var(--ink);
  font-size: 13px;
  font-weight: 600;
  line-height: 20px;
}
.wb-dl {
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.wb-dl > div {
  display: grid;
  grid-template-columns: 56px minmax(0, 1fr);
  gap: 8px;
  align-items: baseline;
}
.wb-dl dt {
  color: var(--mut);
  font-size: 12px;
  line-height: 20px;
}
.wb-dl dd {
  margin: 0;
  font-size: 13px;
  line-height: 20px;
  overflow-wrap: anywhere;
}
.wb-none {
  color: var(--mut);
}
.wb-note {
  margin: 0;
  color: var(--mut);
  font-size: 12px;
  line-height: 18px;
}
.wb-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}
.wb-un {
  border-color: var(--ac);
  color: var(--ac2);
  font-weight: 600;
}
.wb-un :deep(.ic) {
  color: var(--ac);
}
.wb-who {
  color: var(--mut);
  font-size: 12px;
  line-height: 18px;
}

/* ---- control (not blocked): a section like the card's others */
.wb-ctl {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-top: 12px;
  border-top: 1px solid var(--line2);
}
.wb-sh {
  display: flex;
  align-items: center;
  gap: 6px;
  min-height: 24px;
}
.wb-sh > :deep(.ic) {
  width: 15px;
  height: 15px;
  flex: none;
  color: var(--mut);
}
.wb-sht {
  font-size: 13px;
  font-weight: 600;
  line-height: 20px;
}
.wb-hits {
  margin: 0;
  display: flex;
  align-items: flex-start;
  gap: 6px;
  color: var(--mut);
  font-size: 12px;
  line-height: 18px;
}
.wb-hits.warn {
  color: var(--warn);
  font-weight: 600;
}
.wb-hits :deep(.ic) {
  width: 14px;
  height: 14px;
  margin-top: 2px;
  flex: none;
}
.wb-last {
  margin: 0;
  color: var(--mut);
  font-size: 12px;
  line-height: 18px;
  overflow-wrap: anywhere;
}
.wb-go {
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 30px;
  padding: 0 10px;
  border: 1px solid var(--line);
  border-radius: 8px;
  color: var(--ink);
  font-size: 12.5px;
  font-weight: 600;
  line-height: 20px;
}
.wb-go :deep(.ic) {
  width: 14px;
  height: 14px;
  color: var(--mut);
}
.wb-go:hover {
  border-color: #f3c9c4;
  background: var(--crits);
  color: var(--crit);
}
.wb-go:hover :deep(.ic) {
  color: var(--crit);
}

/* ---- dialogs */
.wb-f {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 0 0 16px;
  padding: 0;
  border: 0;
  min-width: 0;
}
.wb-f legend,
.wb-l {
  padding: 0;
  margin-bottom: 8px;
  font-size: 13px;
  font-weight: 600;
  line-height: 20px;
}
.wb-l {
  margin-bottom: 0;
}
.wb-opt-t {
  color: var(--mut);
  font-weight: 400;
}
.wb-seg {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.wb-opt {
  position: relative;
  display: inline-flex;
  align-items: center;
  height: 36px;
  padding: 0 14px;
  border: 1px solid var(--line);
  border-radius: 8px;
  font-size: 13.5px;
  line-height: 20px;
  cursor: pointer;
  user-select: none;
}
.wb-seg.sm .wb-opt {
  height: 32px;
  padding: 0 12px;
  font-size: 13px;
}
.wb-opt input {
  position: absolute;
  opacity: 0;
  pointer-events: none;
}
.wb-opt.on {
  border-color: var(--crit);
  background: var(--crits);
  color: var(--crit);
  font-weight: 600;
}
.wb-opt.off {
  color: #9aa6a0;
  background: #f7f9f8;
  cursor: not-allowed;
}
.wb-opt:has(input:focus-visible) {
  outline: 2px solid var(--ac);
  outline-offset: 2px;
}
.wb-hint {
  margin: 0;
  color: var(--mut);
  font-size: 12px;
  line-height: 18px;
}
.wb-reason textarea {
  width: 100%;
  min-height: 60px;
  padding: 8px 10px;
  border: 1px solid var(--line);
  border-radius: 8px;
  font: inherit;
  font-size: 13.5px;
  line-height: 21px;
  resize: vertical;
}
.wb-reason textarea:focus {
  outline: 2px solid var(--ac);
  outline-offset: 0;
  border-color: transparent;
}
.wb-check {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 13.5px;
  line-height: 21px;
  cursor: pointer;
}
.wb-check input {
  width: 16px;
  height: 16px;
  margin: 2.5px 0 0;
  flex: none;
  accent-color: var(--crit);
}
.wb-check > span {
  display: flex;
  flex-direction: column;
}
.wb-check.off {
  color: var(--mut);
  cursor: not-allowed;
}
.wb-warn {
  margin: 0;
  text-wrap: pretty;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 8px 10px;
  border-radius: 8px;
  background: var(--warns);
  color: #6b4000;
  font-size: 12.5px;
  line-height: 19px;
}
.wb-warn :deep(.ic) {
  width: 15px;
  height: 15px;
  margin-top: 2px;
  flex: none;
  color: var(--warn);
}
.wb-about {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin: 0;
  padding: 12px 0 0;
  border-top: 1px solid var(--line2);
  list-style: none;
}
.wb-about li {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  color: var(--mut);
  font-size: 12.5px;
  line-height: 19px;
}
.wb-about :deep(.ic) {
  width: 14px;
  height: 14px;
  margin-top: 2.5px;
  flex: none;
}
.wb-p {
  margin: 0 0 14px;
  font-size: 13.5px;
  line-height: 21px;
}
.wb-err {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin: 14px 0 0;
  padding: 8px 10px;
  border-radius: 8px;
  background: var(--crits);
  color: var(--crit);
  font-size: 13px;
  line-height: 20px;
}
.wb-err :deep(.ic) {
  width: 15px;
  height: 15px;
  margin-top: 2.5px;
  flex: none;
}
.btn:disabled {
  opacity: 0.6;
}
.wb-do {
  background: var(--crit);
  border-color: var(--crit);
  color: #fff;
  font-weight: 600;
}
.wb-do:hover:not(:disabled) {
  background: #a82217;
}
.wb-do :deep(.ic),
.wb-pri :deep(.ic) {
  color: #fff;
}
.wb-pri {
  background: var(--ac);
  border-color: var(--ac);
  color: #fff;
  font-weight: 600;
}
.wb-pri:hover:not(:disabled) {
  background: var(--ac2);
}
</style>
